import { useEffect } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ExpensesEntitiesExpensableResourceTypeEnum,
  ReadyToReviewExpensesExpensableRatesInput,
  SendExpensableForApprovalMutationVariables,
  SendMileageForReviewMutationVariables,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type ExpenseFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { type MileageFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { type PerDiemFormValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import { type LocalConcept } from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/DailyConcepts'
import { getNewFileUploads } from 'modules/expenses/components/expenses/Form/Buttons/helpers'
import { buildErrorMessages } from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import {
  isExpenseFormValues,
  isMileageFormValues,
  isPerDiemFormValues,
  validatePaymentOption,
} from 'modules/expenses/components/expenses/Form/Buttons/helpers/typechecks'
import { useSendGermanyPerDiemForReview } from 'modules/expenses/components/expenses/New/PerDiemWizard/hooks/useSendGermanyPerDiemForReview'
import {
  ExpensableResourceType,
  normalizeEffectiveOn,
  normalizeTaxes,
  useSendForApproval,
  useSendMileageForApproval,
} from 'modules/expenses/hooks/useExpensable'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import {
  SignableOrganizationPerCountry,
  getSignedForOrganization,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { Expensable } from 'modules/expenses/types/Expensable'
// Type guards for mutation variables
const isMileageData = (
  data: SendExpensableForApprovalMutationVariables | SendMileageForReviewMutationVariables,
  formType: string
): data is SendMileageForReviewMutationVariables => {
  return formType === 'mileage'
}

// This type check considers expenses and per diem because both of them are using the same SendExpensableForApproval
// mutation.
const isExpensableData = (
  data: SendExpensableForApprovalMutationVariables | SendMileageForReviewMutationVariables,
  formType: string
): data is SendExpensableForApprovalMutationVariables => {
  return formType === 'expense' || formType === 'perdiem'
}

interface SendExpensableForApprovalButtonProps {
  formType: ExpensesEntitiesExpensableResourceTypeEnum
  formValues: ExpenseFormValues | PerDiemFormValues | MileageFormValues
  formValid: boolean
  customizationFields: CustomizationField[]
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  expensable?: Expensable
  groupId?: string
  trigger: () => Promise<boolean>
  onClose: () => void
  disabled?: boolean
  onLoadingChange?: (loading: boolean) => void
  isNewPerDiem?: boolean
  modifiedConcepts?: LocalConcept[] | null
}

interface Project {
  projectWorker?: { id: number } | null
  subproject?: { id?: number | null } | null
}

const formatProjects = (projects: Project[]) => {
  return projects
    .filter((project) => project.projectWorker?.id)
    .map((project) => ({
      projectWorkerId: project.projectWorker?.id || 0,
      subprojectId: project.subproject?.id || undefined,
    }))
}

interface NewPerDiemFormPerDiem {
  startDate?: string | null
  endDate?: string | null
  perDiemDestinations?: Array<{
    id: string | number
    locationName: string
    arrivalDate?: string | null
    country?: string | null
    administrativeAreaLevel1?: string | null
    administrativeAreaLevel2?: string | null
    locality?: string | null
  }>
}

function hasNewPerDiemFormPerDiem(
  values: PerDiemFormValues
): values is PerDiemFormValues & { perDiem: NewPerDiemFormPerDiem } {
  return (
    values != null &&
    typeof values === 'object' &&
    'perDiem' in values &&
    values.perDiem != null &&
    typeof values.perDiem === 'object'
  )
}

const formatRates = (
  rates: PerDiemFormValues['perDiemRates']
): ReadyToReviewExpensesExpensableRatesInput[] => {
  if (!rates) return []

  // Filter out rates with null or undefined rateConfigurationId
  // This is because the backend expects a rateConfigurationId defined, but we start with an empty rate as a placeholder
  // The user can still submit with the empty rate to save as draft, but we need to filter it out before sending to the backend
  return rates
    .filter(
      (rate): rate is NonNullable<typeof rate> & { rateConfigurationId: number } =>
        rate.rateConfigurationId !== null && rate.rateConfigurationId !== undefined
    )
    .map(
      (rate): ReadyToReviewExpensesExpensableRatesInput => ({
        id: rate.id,
        rateConfigurationId: rate.rateConfigurationId ?? 0,
        totalAmountCents: rate.totalAmount || 0,
        totalDays: rate.units || 0,
      })
    )
}

const SendExpensableForApprovalButton = ({
  formType,
  formValues,
  expensable,
  groupId,
  formValid,
  trigger,
  onClose,
  customizationFields,
  taxAgencyOrganization,
  disabled = false,
  onLoadingChange,
  isNewPerDiem = false,
  modifiedConcepts = null,
}: SendExpensableForApprovalButtonProps) => {
  const { company, employee } = useCurrentSession()
  const { sendForApproval, loading } = useSendForApproval()
  const { sendMileageForApproval, loading: loadingSendMileageForApproval } =
    useSendMileageForApproval()
  const { sendPerDiemForReview, isSending: isSendingGermanyPerDiem } =
    useSendGermanyPerDiemForReview()

  useEffect(() => {
    onLoadingChange?.(loading || loadingSendMileageForApproval || isSendingGermanyPerDiem)
  }, [loading, loadingSendMileageForApproval, isSendingGermanyPerDiem, onLoadingChange])

  const submitStrategies = {
    expense: (
      values: ExpenseFormValues | PerDiemFormValues
    ): SendExpensableForApprovalMutationVariables => {
      if (!('taxes' in values)) {
        throw new Error('Expense strategy requires ExpenseFormValues')
      }

      const taxes = values.taxes?.map((tax) => {
        return {
          amount: toCents(tax.amount ?? 0),
          base_amount: toCents(tax.base_amount ?? 0),
          percentage: tax.percentage ?? 0,
        }
      })

      const signedForOrganization = getSignedForOrganization({
        filesData: values.files,
        expensable,
        filesIdToRemove: values.filesIdToRemove,
        taxAgencyOrganization,
      })

      const employeeId = values.employeeId || 0
      const isAutomatic = values.creationType === 'automatic'
      const merchantName = isAutomatic ? expensable?.expense?.merchantName : values.merchantName
      const expensableGroupId = expensable?.groupId || Number(groupId) || null
      const newFiles = getNewFileUploads(values.files)

      return {
        id: expensable?.id,
        employeeId: employeeId,
        companyId: company.id,
        amount: values.amount != null ? toCents(values.amount) : expensable?.amount,
        currency: values.currency,
        category: values.category,
        subcategory: values.subcategory,
        documentNumber: values.documentNumber,
        description: values.description,
        merchantName: merchantName,
        merchantTin: values.merchantTin,
        paymentMethod: values.paymentMethod,
        reimbursableCurrency: values.reimbursableCurrency,
        exchangeRate: values.exchangeRate,
        documentType: values.documentType,
        groupId: expensableGroupId,
        effectiveOn: normalizeEffectiveOn(values.effectiveOn),
        userMerchant: values.merchantName,
        taxes: normalizeTaxes({
          taxes: taxes,
          vatAmount: taxes[0]?.amount ?? null,
          customizationFields,
        }),
        resourceType: ExpensableResourceType.Expense,
        projects: values.projects.map((project) => ({
          projectWorkerId: project.projectWorker?.id ?? 0,
          subprojectId: project.subproject?.id,
        })),
        budgetId: values.budgetId,
        reimbursableAmount: values.reimbursableAmount
          ? toCents(values.reimbursableAmount)
          : expensable?.reimbursableAmount,
        files: newFiles,
        filesIdToRemove: values.filesIdToRemove,
        signedForOrganization: signedForOrganization,
        payment: validatePaymentOption(values.payment),
        internalReference: values.internalReference,
        costCenters: values.cost_centers,
      }
    },

    perdiem: (
      values: ExpenseFormValues | PerDiemFormValues
    ): SendExpensableForApprovalMutationVariables => {
      if (!('perDiemRates' in values)) {
        throw new Error('PerDiem strategy requires PerDiemFormValues')
      }

      const employeeId = values.employeeId || 0
      const expensableGroupId = expensable?.groupId || Number(groupId) || null
      const newFiles = getNewFileUploads(values.files)

      const calculatedAmount =
        values.perDiemRates?.reduce((acc, rate) => acc + rate.totalAmount, 0) || 0

      return {
        id: expensable?.id,
        groupId: expensableGroupId,
        amount: calculatedAmount != null ? calculatedAmount : expensable?.amount,
        employeeId: employeeId,
        companyId: company.id,
        effectiveOn: normalizeEffectiveOn(values.start_date),
        resourceType: ExpensableResourceType.PerDiem,
        projects: formatProjects(values.projects),
        budgetId: values.budgetId,
        files: newFiles,
        filesIdToRemove: values.filesIdToRemove || [],
        internalReference: values.internalReference,
        perDiem: {
          amountCents: calculatedAmount != null ? calculatedAmount : expensable?.amount,
          budgetId: values.budgetId,
          companyId: company.id,
          currency: values.currency,
          description: values.description,
          employeeId: employeeId,
          endDate: values.end_date,
          files: newFiles,
          from: values.from,
          projects: formatProjects(values.projects),
          startDate: values.start_date,
          subcategory: values.subcategory,
          to: values.to,
          rates: formatRates(values.perDiemRates || []),
        },
        taxes: [],
        costCenters: values.cost_centers,
      }
    },
    mileage: (values: MileageFormValues): SendMileageForReviewMutationVariables => {
      const employeeId = values.employeeId || 0
      const newFiles = getNewFileUploads(values.files)

      return {
        id: expensable?.id,
        amount: values.amount != null ? toCents(values.amount) : expensable?.amount,
        budgetId: values.budgetId,
        calculatedMileage: toCents(values.calculatedMileage),
        companyId: company.id,
        currency: values.currency,
        description: values.description,
        destinationLatitude: values.destinationLatitude
          ? parseFloat(values.destinationLatitude)
          : null,
        destinationLongitude: values.destinationLongitude
          ? parseFloat(values.destinationLongitude)
          : null,
        effectiveOn: normalizeEffectiveOn(values.effectiveOn),
        employeeId: employeeId,
        files: newFiles,
        filesIdToRemove: values.filesIdToRemove,
        from: values.from,
        groupId: Number(groupId),
        internalReference: values.internalReference,
        mileage: toCents(values.mileage),
        originLatitude: values.originLatitude ? parseFloat(values.originLatitude) : null,
        originLongitude: values.originLongitude ? parseFloat(values.originLongitude) : null,
        projects: values.projects.map((project) => ({
          projectWorkerId: project.projectWorker?.id ?? 0,
          subprojectId: project.subproject?.id,
        })),
        rate: values.rate?.toString(),
        reimbursableAmount: values.amount,
        reimbursableCurrency: values.currency,
        roundTrip: values.roundTrip,
        stops: values.stops?.map(
          (
            stop: { name?: string | null; latitude?: string | null; longitude?: string | null },
            i: number
          ) => ({
            name: stop.name || '',
            latitude: stop.latitude ? parseFloat(stop.latitude) : null,
            longitude: stop.longitude ? parseFloat(stop.longitude) : null,
            position: i,
          })
        ),
        subcategory: values.subcategory,
        to: values.to,
        units: values.units,
        costCenters: values.cost_centers,
      }
    },
  }

  const buildSubmitData = ():
    | SendExpensableForApprovalMutationVariables
    | SendMileageForReviewMutationVariables => {
    if (formType === 'expense' && isExpenseFormValues(formValues, formType)) {
      return submitStrategies.expense(formValues)
    } else if (formType === 'perdiem' && isPerDiemFormValues(formValues, formType)) {
      return submitStrategies.perdiem(formValues)
    } else if (formType === 'mileage' && isMileageFormValues(formValues, formType)) {
      return submitStrategies.mileage(formValues)
    }

    throw new Error(`No strategy found for form type: ${formType}`)
  }

  const triggerValidation = async () => {
    const isValid = await trigger()
    return isValid
  }

  const handleSendMileageForApproval = async (
    submitData: SendMileageForReviewMutationVariables
  ) => {
    const result = await sendMileageForApproval(submitData)

    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })

    if (!handled.success) return handled
    const updatedExpensable = handled.data?.expensable
    if (!updatedExpensable) return handled

    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.modals.new_expense.submitted_for_review_successfully'),
    })
    onClose()
  }

  const handleSendExpensableForApproval = async (
    submitData: SendExpensableForApprovalMutationVariables
  ) => {
    const result = await sendForApproval(submitData)

    const handled = handleResult(result, (errors) => {
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })

    if (!handled.success) return handled
    const updatedExpensable = handled.data?.expensable
    if (!updatedExpensable) return handled

    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.modals.new_expense.submitted_for_review_successfully'),
    })
    onClose()
  }

  const handleSendNewPerDiemForApproval = async () => {
    if (!expensable || !isPerDiemFormValues(formValues, formType)) return

    const values = formValues
    const formPerDiem = hasNewPerDiemFormPerDiem(values) ? values.perDiem : undefined
    const perDiem = expensable.perDiem
    if (!perDiem) return

    const rawDestinations = formPerDiem?.perDiemDestinations ?? perDiem.perDiemDestinations ?? []
    const destinationsWithArrival = rawDestinations.filter((dest) => dest.arrivalDate != null)
    if (!destinationsWithArrival.length) return

    const conceptsToSend =
      modifiedConcepts ||
      perDiem.perDiemConcepts?.map((concept) => ({
        id: concept.id,
        date: concept.date,
        breakfastProvided: concept.breakfastProvided,
        lunchProvided: concept.lunchProvided,
        dinnerProvided: concept.dinnerProvided,
        accommodationProvided: concept.accommodationProvided,
      }))

    const startDateStr = formPerDiem?.startDate ?? perDiem.startDate
    const startDate = startDateStr ? new Date(startDateStr) : null
    const endDateStr = formPerDiem?.endDate ?? perDiem.endDate
    const endDate = endDateStr ? new Date(endDateStr) : null

    if (!startDate || !endDate) return

    const filesList: unknown[] = values.files || []
    const newFiles = filesList.filter((file): file is File => file instanceof File)

    const result = await sendPerDiemForReview(
      {
        startDate,
        endDate,
        destinations: destinationsWithArrival.map((dest) => ({
          id: String(dest.id),
          locationName: dest.locationName,
          arrivalDatetime: new Date(String(dest.arrivalDate)),
          locationData: {
            country: dest.country ?? '',
            administrativeAreaLevel1: dest.administrativeAreaLevel1 ?? '',
            administrativeAreaLevel2: dest.administrativeAreaLevel2 ?? '',
            locality: dest.locality ?? '',
          },
        })),
        dayExclusions: (conceptsToSend || []).map((concept) => ({
          date: new Date(concept.date),
          breakfastProvided: concept.breakfastProvided,
          lunchProvided: concept.lunchProvided,
          dinnerProvided: concept.dinnerProvided,
          accommodationProvided: concept.accommodationProvided,
        })),
        description: values.description ?? undefined,
        internalReference: values.internalReference ?? undefined,
        budgetId: values.budgetId ?? undefined,
        files: newFiles,
        employeeId: values.employeeId || employee.id,
        costCenters: values.cost_centers ?? undefined,
      },
      company.id,
      expensable.id
    )

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.modals.new_expense.submitted_for_review_successfully'),
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: result.error || i18n.t('errors.generic_error'),
        time: 3000,
      })
    }
  }

  const handleSendForApproval = async () => {
    if (isNewPerDiem && formType === 'perdiem') {
      return await handleSendNewPerDiemForApproval()
    }

    const submitData = buildSubmitData()

    if (isMileageData(submitData, formType)) {
      return await handleSendMileageForApproval(submitData)
    } else if (isExpensableData(submitData, formType)) {
      return await handleSendExpensableForApproval(submitData)
    }
  }

  const isLoading = loading || isSendingGermanyPerDiem

  return (
    <Button
      onClick={handleSendForApproval}
      disabled={isLoading || !formValid || disabled}
      loading={isLoading}
      iconName={isLoading ? 'upload' : undefined}
      type="primary"
    >
      {i18n.t('expenses_v2.buttons.send_for_approval')}
    </Button>
  )
}

export default SendExpensableForApprovalButton
